import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';
import moment from "moment";

import useToolTip from '@helpers/hooks/use-tool-tip';
import OutsideClickHandler from '@helpers/hoc/close/outside-click-handler';

import ContextModal from '@components/context-modal';
import { PROGRESS, ACCURACY } from '@components/accuracy-progress-toggle/accuracy-progress-constants';
import SvgIcon from '@components/svg-icon/svg-icon';
import ToolTip from '@components/tooltip/tooltip';

import { selectAssignmentHashKey } from '@containers/upf-assign/upf-assign-actions';

import useContextModal from '@helpers/hooks/use-context-modal';
import { CrownMedium } from '@helpers/icon-dictionary';

import ContextualMenu from './contextual-menu/contextual-menu';
import {
  getProgressResultText,
  getAccuracyResultText,
  getProgressPercentage,
  checkIsAssignmentCompleted
} from './helpers';
import getTranslations from './translations';

import {
  Cell,
  ResultWrapper,
  Result,
  HoverWrapper
} from './styled';

const StatusCell = props => {
  const { assignment, assignmentHashKey, currentResultFilter, isSelected, learningJourneyId, studentId } = props;

  /**
   * Hooks
   */
  const dispatch = useDispatch();
  const { i18n } = useLocalisation();
  const { targetRef, showToolTip, onHoverHandlers } = useToolTip();

  const {
    showContextModal,
    closeContextMenu,
    contentModalProps,
    contentModalProps: { show: isContextModalOpen }
  } = useContextModal({ defaultTargetRef: targetRef });

  /**
   * Local variables
   */
  const {
    currentAssignmentId,
    resultAccuracy,
    resultStepsCompleted,
    currentAssignmentUnavailableForStudent
  } = assignment || {};

  const progressPercentage = getProgressPercentage(assignment);
  const isProgressFilter = currentResultFilter === PROGRESS;
  const isCompleted = checkIsAssignmentCompleted(assignment);
  const resultText = isProgressFilter ? getProgressResultText(assignment) : getAccuracyResultText(assignment);
  const backgroundColorCheckValue = isProgressFilter ? progressPercentage : resultAccuracy;
  const hasCurrentAssigned = !!currentAssignmentId;
  const showCurrentAssignmentTriangle = hasCurrentAssigned && !!resultText;
  const showCurrentAssigned = hasCurrentAssigned && resultStepsCompleted === null;
  const showResult = progressPercentage > 0;

  /**
   * Translations
   */
  const { CURRENTLY_ASSIGNED_TEXT, OVERDUE_ASSIGNMENT_REMOVED } = getTranslations({ i18n });

  /**
   * Functions
   */
  const selectCell = () => {
    dispatch(selectAssignmentHashKey({ assignmentHashKey, learningJourneyId, studentId }));
  };

  const onClickHandle = () => {
    if (hasCurrentAssigned || showResult) {
      onHoverHandlers.onMouseLeave();
      return showContextModal();
    }
    return selectCell();
  };

  const renderResult = () => <Result>{resultText}</Result>;

  const renderProgressResults = () => (
    <>
      {isCompleted && <SvgIcon dataTestId="status-completed-icon" icon={CrownMedium} />}
      {!isCompleted && renderResult()}
    </>
  );

  const getDateFormat = (date) => moment(date).format('DD/MM/YY');

  const getTooltipContent = () => {
    if(currentAssignmentUnavailableForStudent) {
      return OVERDUE_ASSIGNMENT_REMOVED
    } else if (hasCurrentAssigned) {
      return `${CURRENTLY_ASSIGNED_TEXT} ${getDateFormat(assignment.currentAssignmentStartDate)}`
    }

    return '';
  }

  return (
    <Cell
      ref={targetRef}
      backgroundColorCheckValue={backgroundColorCheckValue}
      data-testid="status-cell"
      isSelected={isSelected}
      showCurrentAssigned={showCurrentAssigned}
      showCurrentAssignmentTriangle={showCurrentAssignmentTriangle}
      showResult={showResult}
      onClick={onClickHandle}
      {...onHoverHandlers}
    >
      {hasCurrentAssigned && showToolTip && !isContextModalOpen && (
        <ToolTip variant="dark" targetRef={targetRef}>
          <HoverWrapper>
            {getTooltipContent()}
          </HoverWrapper>
        </ToolTip>
      )}

      {showResult && (
        <ResultWrapper data-testid="status-result">
          {isProgressFilter && renderProgressResults()}
          {!isProgressFilter && renderResult()}
        </ResultWrapper>
      )}

      <OutsideClickHandler callback={closeContextMenu} forButton="contextualMenu">
        <ContextModal {...contentModalProps} borderRadius={3} modalHeight={212} overflow="visible">
          <ContextualMenu
            assignmentId={currentAssignmentId}
            hasOnlyResult={showResult && !hasCurrentAssigned}
            studentId={studentId}
            learningJourneyId={learningJourneyId}
            isCellSelected={isSelected}
            selectCell={selectCell}
          />
        </ContextModal>
      </OutsideClickHandler>
    </Cell>
  );
};

StatusCell.propTypes = {
  assignment: PropTypes.object,
  assignmentHashKey: PropTypes.string.isRequired,
  currentResultFilter: PropTypes.oneOf([PROGRESS, ACCURACY, '']).isRequired,
  learningJourneyId: PropTypes.number.isRequired,
  studentId: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired
};

StatusCell.defaultProps = {
  assignment: {}
};

export default StatusCell;
